import { useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfig, type ListItem } from '../../context/ConfigContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';
import { Locations } from './Locations';

// Locations isn't a list_items category (it's its own table, referenced by
// Position/Benefit Eligibility Rule FKs) — this sentinel key routes to the
// dedicated <Locations> page instead of the generic <ListSection>, while
// still living in the same section picker for a single "Lists" home.
const LOCATIONS_KEY = '__locations';

// TS port of the source (Falah) app's pages/system/Lists.jsx. SECTIONS is
// trimmed to VNPF's actual ListItem::KEYS (api/app/Models/ListItem.php) —
// Falah's Programmes/Education/Volunteers/PrayerTime/Singpass list keys
// (agent_type, vendor_type, donor_type, finance_fund, zakat_category, ...)
// don't exist on this backend and were dropped rather than shown as dead
// sections that 404 on save.
// Temporarily hidden — see SHOW_RESOURCES_LISTS below (mirrors AppShell's
// SHOW_RESOURCES_NAV and ReportsIndex's SHOW_RESOURCES_REPORTS). The list
// items/keys themselves are untouched, just not offered as an editable
// section while Resources stays hidden elsewhere in the portal.
const SHOW_RESOURCES_LISTS = false;

const RESOURCES_SECTIONS: { key: string; title: string }[] = [
  { key: 'asset_category', title: 'Asset Categories' },
  { key: 'asset_department', title: 'Asset Departments' },
  { key: 'booking_status', title: 'Booking Status' },
  { key: 'facility_status', title: 'Facility Status' },
  { key: 'inventory_category', title: 'Inventory Categories' },
  { key: 'inventory_unit', title: 'Inventory Units' },
];

const SECTIONS: { key: string; title: string }[] = [
  ...(SHOW_RESOURCES_LISTS ? RESOURCES_SECTIONS : []),
  { key: 'candidate_stage', title: 'Candidate Stages' },
  { key: 'compensation_change_reason', title: 'Compensation Change Reasons' },
  { key: 'department', title: 'Departments' },
  { key: 'employee_document_type', title: 'Employee Document Types' },
  { key: 'employment_status', title: 'Employment Statuses' },
  { key: 'invoice_type', title: 'Invoice Types' },
  { key: 'job_category', title: 'Job Categories' },
  { key: 'user_designation', title: 'Job Titles' },
  { key: 'leave_type', title: 'Leave Types' },
  { key: 'license', title: 'Licenses' },
  { key: LOCATIONS_KEY, title: 'Locations' },
  { key: 'membership', title: 'Memberships' },
  { key: 'qualification_level', title: 'Qualification Levels' },
  { key: 'member_type', title: 'Member Types' },
  { key: 'payment_method', title: 'Payment Methods' },
  { key: 'payroll_addition_type', title: 'Payroll Addition Types' },
  { key: 'payroll_deduction_type', title: 'Payroll Deduction Types' },
  { key: 'performance_review_period', title: 'Performance Review Periods' },
  { key: 'position_status', title: 'Position Status' },
  { key: 'quotation_status', title: 'Quotation Statuses' },
  { key: 'relationship', title: 'Relationships' },
  { key: 'identity_type', title: 'Identity Types' },
  { key: 'industry_sector', title: 'Industry Sectors' },
  { key: 'goods_receipt_condition', title: 'Receiving Conditions' },
  { key: 'purchase_request_type', title: 'Request Types' },
  { key: 'separation_type', title: 'Separation Types' },
  { key: 'skill', title: 'Skills' },
  { key: 'timesheet_activity', title: 'Timesheet Activities' },
  { key: 'training_category', title: 'Training Categories' },
  { key: 'vacancy_department', title: 'Vacancy Departments' },
].sort((a, b) => a.title.localeCompare(b.title));

interface ItemForm {
  value: string;
  label: string;
  sort_order: number | string;
}

const emptyForm: ItemForm = { value: '', label: '', sort_order: 0 };

interface ListSectionProps {
  listKey: string;
  title: string;
  items: ListItem[];
  onChanged: () => void;
}

function ListSection({ listKey, title, items, onChanged }: ListSectionProps) {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [editing, setEditing] = useState<'new' | ListItem | null>(null);
  const [form, setForm] = useState<ItemForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const openNew = () => {
    setForm({ ...emptyForm, sort_order: items.length });
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: ListItem) => {
    setForm({ value: item.value, label: item.label, sort_order: item.sort_order });
    setErrors({});
    setEditing(item);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const payload = { ...form, list_key: listKey, sort_order: Number(form.sort_order) || 0 };
    try {
      if (editing === 'new') await api.post('/list-items', payload);
      else if (editing) await api.put(`/list-items/${editing.id}`, payload);
      setEditing(null);
      onChanged();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: ListItem) => {
    if (!(await confirmDelete(`Delete "${item.label}"?`))) return;
    try {
      await api.delete(`/list-items/${item.id}`);
      onChanged();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const set = (key: keyof ItemForm) => (e: ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [key]: e.target.value });

  return (
    <div className="card">
      <div className="list-section-header">
        <h2>{title}</h2>
        <button className="btn btn-primary" onClick={openNew}>
          + Add
        </button>
      </div>
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Label</th>
              <th>Value</th>
              <th>Order</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.id}>
                <td>{item.label}</td>
                <td className="muted">{item.value}</td>
                <td>{item.sort_order}</td>
                <td className="right">
                  <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                  <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={4} className="muted center">
                  No items yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? `Add ${title} option` : `Edit ${title} option`} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error">{errors.general[0]}</div>}
            <label className="span-2">
              Label *
              <input value={form.label} onChange={set('label')} required />
              {errors.label && <small className="error">{errors.label[0]}</small>}
            </label>
            <label className="span-2">
              Value (internal code) *
              <input value={form.value} onChange={set('value')} required />
              <small className="muted">Letters, numbers, dashes and underscores only.</small>
              {errors.value && <small className="error">{errors.value[0]}</small>}
            </label>
            <label className="span-2">
              Sort order
              <input type="number" min="0" value={form.sort_order} onChange={set('sort_order')} />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export function Lists() {
  const { lists, reload } = useConfig();
  const [selectedKey, setSelectedKey] = useState(SECTIONS[0].key);
  const selected = SECTIONS.find((s) => s.key === selectedKey) ?? SECTIONS[0];

  return (
    <>
      <header className="page-header">
        <h1>Lists</h1>
      </header>
      <p className="muted">Manage the option values used in dropdowns throughout the portal.</p>

      <div className="toolbar">
        <select value={selectedKey} onChange={(e) => setSelectedKey(e.target.value)}>
          {SECTIONS.map((section) => (
            <option key={section.key} value={section.key}>
              {section.title}
            </option>
          ))}
        </select>
      </div>

      {selected.key === LOCATIONS_KEY ? (
        <Locations />
      ) : (
        <ListSection
          key={selected.key}
          listKey={selected.key}
          title={selected.title}
          items={lists[selected.key] ?? []}
          onChanged={reload}
        />
      )}
    </>
  );
}
